import UserCompetitions from '@/components/Competition/UserCompetitions'
import { ExpandableBlock, LoadingView } from '@/components/FormElement'
import UserLayout from '@/components/Layouts/UserLayout'
import { useAuth } from '@/hooks/Auth'
import { useUserBalance } from '@/hooks/Balance'
import { useLanguage } from '@/hooks/Contexts'
import { formatWithGBP } from '@/utils/FloatNumber'
import { svgs } from '@/utils/Images'
import Link from 'next/link'
import { useEffect, useMemo, useState, useCallback } from 'react'
import UserLogout from '@/components/UserLogout'

const Dashboard = () => {
  const { user: userStr, auth: authStr, wallet: walletStr } = useLanguage()

  const { user, logout } = useAuth({ middleware: 'auth' })
  const [isModalOpen, setIsModalOpen] = useState(false) // Added state for modal

  const [isLoading, setIsLoading] = useState(true)
  useEffect(() => {
    if (user === undefined) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [user])

  const handleLogout = useCallback(() => {
    logout?.()
    setIsModalOpen(false) // Close modal after logging out
  }, [logout])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false) // Close modal without logging out
  }, [])

  const dashboardOptions = useMemo(
    () => [
      { path: '/user/orders', title: userStr.orders, desc: userStr.dashboard_desc_1, link: userStr.dashboard_link_1, icon: svgs.shopping_bag },
      // { path: '/user/addresses', title: userStr.address, desc: userStr.dashboard_desc_2, link: userStr.dashboard_link_2, icon: svgs.map_pin },
      { path: '/user/details', title: userStr.details, desc: userStr.dashboard_desc_2, link: userStr.dashboard_link_3, icon: svgs.user_solid },
    ],
    [userStr]
  )

  const displayName = user?.display_name ?? user?.profile?.name ?? user?.email.substring(0, user?.email.indexOf('@'))

  const { cashTotal, creditTotal } = useUserBalance(user)

  return (
    <UserLayout title={userStr.dashboard}>
      <LoadingView show={isLoading} />
      {isModalOpen && <UserLogout handleCloseModal={handleCloseModal} handleLogout={handleLogout} />}
      {!isLoading && (
        <>
          <div className='flex w-full flex-wrap items-center justify-between whitespace-nowrap md:justify-start'>
            <span className='text-xl font-semibold'>
              {userStr.hello} {displayName}&nbsp;
            </span>
            <span className='text-sm'>
              ({userStr.not_user} {displayName}?&nbsp;
              <a
                href='/'
                onClick={e => {
                  e.preventDefault()
                  setIsModalOpen(true)
                }}
                className='inline font-semibold text-error underline hover:font-bold'
              >
                {authStr.logout}
              </a>
              )
            </span>
          </div>
          <div className='mt-4 text-sm'>
            {dashboardOptions.map((option, i) => (
              <span key={`dashboard-link-${i}`}>
                {option.desc}
                <Link href={option.path}>
                  <span className='clickable-text font-semibold'>{option.link}</span>
                </Link>
              </span>
            ))}
          </div>
          <ExpandableBlock
            title={walletStr.wallet_balance}
            titleTxtCls='text-cyan-v2'
            openDefault={true}
            outerCls='mt-4'
            innerCls='flex flex-col lg:flex-row lg:items-center'
          >
            {[
              { link: '/user/wallet/withdraw', label: walletStr.cash_balance, value: cashTotal, btn: walletStr.withdraw, testid: '-accCentre-withdraw' },
              {
                link: '/user/wallet/top-up',
                label: walletStr.site_credit,
                value: creditTotal,
                btn: walletStr.buy_site_credit,
                testid: '-accCentre-buyCredit',
              },
            ].map(item => (
              <div
                key={item.link}
                className='flex w-full flex-1 flex-wrap items-center gap-y-2 whitespace-nowrap last:mt-4 last:border-t last:pt-4 last:lg:mt-0 last:lg:border-l last:lg:border-t-0 last:lg:pl-4 last:lg:pt-0'
              >
                <div className='font-semibold'>{item.label}</div>
                <div className='mx-4 font-semibold' data-testid={'txt' + item.testid}>
                  {formatWithGBP(item.value)}
                </div>
                <Link href={item.link}>
                  <button className='btn-rd-amber btn-hover-amber h-9 w-full px-4 sm:w-36' type='button' data-testid={'btn' + item.testid}>
                    {item.btn}
                  </button>
                </Link>
              </div>
            ))}
          </ExpandableBlock>

          <div className='mb-2 mt-4 text-xl font-semibold md:mt-6'>{userStr.ur_comp_entries}</div>
          <UserCompetitions />
        </>
      )}
    </UserLayout>
  )
}

export default Dashboard
